import React from "react";
import { ControlRow, Slider, StepButton, Value } from "./configuration.styles";
import { clamp, formatOffset, roundToStep, STEP } from "./helpers";

interface IProps {
  id: string;
  label: string;
  /** Absolute limit in °C: the slider runs from -limit to +limit. */
  limit: number;
  /** What the unit has (as far as jcore knows). */
  current: number;
  /** The unsent value, if the user moved the slider. */
  value?: number;
  disabled: boolean;
  onChange: (value: number) => void;
}

/**
 * Cursor for a probe offset. The unit replaces its offset with the value sent (it does not add to it), so the
 * slider shows the absolute offset and the difference to the current one.
 */
const OffsetControl: React.FC<IProps> = ({
  id,
  label,
  limit,
  current,
  value,
  disabled,
  onChange
}) => {
  const shown = value ?? current;
  const delta = roundToStep(shown - current);
  const set = (next: number) => onChange(clamp(roundToStep(next), -limit, limit));

  return (
    <ControlRow>
      <StepButton
        type="button"
        aria-label={`${label} -${STEP}`}
        disabled={disabled || shown <= -limit}
        onClick={() => set(shown - STEP)}
      >
        −
      </StepButton>
      <Slider
        id={id}
        aria-label={label}
        min={-limit}
        max={limit}
        step={STEP}
        value={shown}
        disabled={disabled}
        onChange={event => set(Number(event.target.value))}
      />
      <StepButton
        type="button"
        aria-label={`${label} +${STEP}`}
        disabled={disabled || shown >= limit}
        onClick={() => set(shown + STEP)}
      >
        +
      </StepButton>
      <Value changed={delta !== 0}>{formatOffset(shown)}</Value>
      {delta !== 0 && (
        <span className="text-sm">{`(${delta > 0 ? "+" : ""}${delta.toFixed(1)})`}</span>
      )}
    </ControlRow>
  );
};

export default OffsetControl;
